// PasswordPage.jsx (исправленный)
import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {
    ArrowLeftOutlined,
    CopyOutlined,
    DeleteOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
    KeyOutlined,
    PlusOutlined,
    SearchOutlined,
    UserOutlined
} from '@ant-design/icons';
import {passwordAPI} from '@/api/endpoints/passwords.js';
import styles from '@/styles/pages/password/PasswordPage.module.css';

const PasswordPage = () => {
    const {uuid: projectUUID} = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [passwords, setPasswords] = useState([]);
    const [writeTypes, setWriteTypes] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [editingPassword, setEditingPassword] = useState(null);
    const [showPassword, setShowPassword] = useState({});
    const [passwordForm, setPasswordForm] = useState({
        login: '',
        title: '',
        password: '',
        write_type: 'other'
    });

    // Загрузка паролей
    const fetchPasswords = useCallback(async () => {
        setLoading(true);
        const response = await passwordAPI.get(projectUUID, false);
        if (response.success) {
            setPasswords(response.data.passwords || []);
            setWriteTypes(response.data.write_types || []);
        }
        setLoading(false);
    }, [projectUUID]);

    useEffect(() => {
        fetchPasswords();
    }, [fetchPasswords]);

    // Фильтрация паролей
    const filteredPasswords = passwords.filter(pwd =>
        pwd.login?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pwd.write_type?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Копирование в буфер
    const handleCopy = async (text, field) => {
        try {
            await navigator.clipboard.writeText(text);
            alert(`${field} скопирован в буфер`);
        } catch (err) {
            alert('Не удалось скопировать');
        }
    };

    // Переключение видимости пароля
    const togglePasswordVisibility = (id) => {
        setShowPassword(prev => ({...prev, [id]: !prev[id]}));
    };

    // Сохранение пароля
    const handleSavePassword = async () => {
        if (!passwordForm.login.trim()) {
            alert('Введите логин');
            return;
        }
        if (!passwordForm.password.trim()) {
            alert('Введите пароль');
            return;
        }

        let response;
        if (editingPassword) {
            passwordForm.id = editingPassword.id
            response = await passwordAPI.post(projectUUID, passwordForm, false);
        } else {
            response = await passwordAPI.post(projectUUID, passwordForm);
        }

        if (response.success) {
            setShowPasswordModal(false);
            setEditingPassword(null);
            setPasswordForm({
                login: '',
                password: '',
                title: '',
                write_type: 'other'
            });
            fetchPasswords();
        }
    };

    // Удаление пароля
    const handleDeletePassword = async (passwordId) => {
        if (window.confirm('Вы уверены, что хотите удалить этот пароль?')) {
            const response = await passwordAPI.delete(projectUUID, {password_id: passwordId});
            if (response.success) {
                fetchPasswords();
            }
        }
    };

    // Редактирование пароля
    const handleEditPassword = (password) => {
        setEditingPassword(password);
        setPasswordForm({
            login: password.login || '',
            title: password.title || '',
            password: password.password || '',
            write_type: password.write_type || 'other'
        });
        setShowPasswordModal(true);
    };

    const handleBack = () => {
        navigate(-1);
    };

    // Генерация случайного пароля
    const generateRandomPassword = () => {
        const length = 16;
        const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+';
        let password = '';
        for (let i = 0; i < length; i++) {
            password += charset.charAt(Math.floor(Math.random() * charset.length));
        }
        setPasswordForm({...passwordForm, password});
    };

    // Получение цвета для типа
    const getWriteTypeColor = (type) => {
        const colors = {
            htpasswd: '#58a6ff',
            other: '#7d8590'
        };
        return colors[type] || '#7d8590';
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner}></div>
                <p>Загрузка паролей...</p>
            </div>
        );
    }

    return (
        <div className={styles.container}>

            <div className={styles.content}>
                {/* Шапка */}
                <div className={styles.header}>
                    <button className={styles.backButton} onClick={handleBack}>
                        <ArrowLeftOutlined/> Назад
                    </button>
                    <h1 className={styles.title}>
                        <KeyOutlined className={styles.titleIcon}/>
                        Менеджер паролей
                    </h1>
                    <button
                        className={styles.addButton}
                        onClick={() => {
                            setEditingPassword(null);
                            setPasswordForm({
                                login: '',
                                title: '',
                                password: '',
                                write_type: 'other'
                            });
                            setShowPasswordModal(true);
                        }}
                    >
                        <PlusOutlined/> Добавить пароль
                    </button>
                </div>

                {/* Поиск */}
                <div className={styles.searchBar}>
                    <SearchOutlined className={styles.searchIcon}/>
                    <input
                        type="text"
                        placeholder="Поиск по логину или типу..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.searchInput}
                    />
                </div>

                {/* Статистика */}
                <div className={styles.stats}>
                    <div className={styles.statCard}>
                        <span className={styles.statValue}>{passwords.length}</span>
                        <span className={styles.statLabel}>Всего паролей</span>
                    </div>
                    <div className={styles.statCard}>
            <span className={styles.statValue}>
              {passwords.filter(p => p.write_type === 'htpasswd').length}
            </span>
                        <span className={styles.statLabel}>HTPASSWD</span>
                    </div>
                    <div className={styles.statCard}>
            <span className={styles.statValue}>
              {passwords.filter(p => p.write_type === 'other').length}
            </span>
                        <span className={styles.statLabel}>Другие</span>
                    </div>
                </div>

                {/* Список паролей */}
                <div className={styles.passwordGrid}>
                    {filteredPasswords.map((pwd) => (
                        <div key={pwd.id} className={styles.passwordCard}>
                            <div className={styles.cardHeader}>
                                {pwd.title ? (
                                    <div className={styles.cardTitle}>
                                        <h3>{pwd.title}</h3>
                                    </div>
                                ) : (
                                    <div className={styles.cardTitle}>
                                        <KeyOutlined className={styles.cardIcon}/>
                                        <h3>ID: {pwd.id}</h3>
                                    </div>
                                )}
                                <div className={styles.cardActions}>
                                    <button
                                        className={styles.editButton}
                                        onClick={() => handleEditPassword(pwd)}
                                    >
                                        ✏️
                                    </button>
                                    <button
                                        className={styles.deleteButton}
                                        onClick={() => handleDeletePassword(pwd.id)}
                                    >
                                        <DeleteOutlined/>
                                    </button>
                                </div>
                            </div>

                            <div className={styles.typeBadge} style={{
                                backgroundColor: getWriteTypeColor(pwd.write_type) + '20',
                                color: getWriteTypeColor(pwd.write_type)
                            }}>
                                {pwd.write_type?.toUpperCase() || 'OTHER'}
                            </div>

                            <div className={styles.field}>
                <span className={styles.fieldLabel}>
                  <UserOutlined/> Логин:
                </span>
                                <div className={styles.fieldValue}>
                                    <span>{pwd.login}</span>
                                    <button
                                        className={styles.copyButton}
                                        onClick={() => handleCopy(pwd.login, 'Логин')}
                                    >
                                        <CopyOutlined/>
                                    </button>
                                </div>
                            </div>

                            <div className={styles.field}>
                <span className={styles.fieldLabel}>
                  <KeyOutlined/> Пароль:
                </span>
                                <div className={styles.fieldValue}>
                  <span className={styles.passwordValue}>
                    {showPassword[pwd.id] ? pwd.password : '••••••••••••••••'}
                  </span>
                                    <button
                                        className={styles.visibilityButton}
                                        onClick={() => togglePasswordVisibility(pwd.id)}
                                    >
                                        {showPassword[pwd.id] ? <EyeInvisibleOutlined/> : <EyeOutlined/>}
                                    </button>
                                    <button
                                        className={styles.copyButton}
                                        onClick={() => handleCopy(pwd.password, 'Пароль')}
                                    >
                                        <CopyOutlined/>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {filteredPasswords.length === 0 && !loading && (
                    <div className={styles.emptyState}>
                        <KeyOutlined className={styles.emptyIcon}/>
                        <p>Нет сохранённых паролей</p>
                        <button
                            className={styles.addButtonEmpty}
                            onClick={() => setShowPasswordModal(true)}
                        >
                            <PlusOutlined/> Добавить первый пароль
                        </button>
                    </div>
                )}
            </div>

            {/* Модальное окно */}
            {showPasswordModal && (
                <div className={styles.modalOverlay} onClick={() => setShowPasswordModal(false)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2>{editingPassword ? 'Редактирование пароля' : 'Новый пароль'}</h2>
                            <button className={styles.modalClose} onClick={() => setShowPasswordModal(false)}>
                                ✕
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            <div className={styles.formGroup}>
                                <label>Заголовок</label>
                                <input
                                    type="text"
                                    value={passwordForm.title}
                                    onChange={(e) => setPasswordForm({...passwordForm, title: e.target.value})}
                                    placeholder="Описание пароля"
                                    className={styles.input}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Логин *</label>
                                <input
                                    type="text"
                                    value={passwordForm.login}
                                    onChange={(e) => setPasswordForm({...passwordForm, login: e.target.value})}
                                    placeholder="user@example.com или имя пользователя"
                                    className={styles.input}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Пароль *</label>
                                <div className={styles.passwordInputGroup}>
                                    <input
                                        type="text"
                                        value={passwordForm.password}
                                        onChange={(e) => setPasswordForm({...passwordForm, password: e.target.value})}
                                        placeholder="Введите или сгенерируйте пароль"
                                        className={styles.input}
                                    />
                                    <button
                                        type="button"
                                        className={styles.generateButton}
                                        onClick={generateRandomPassword}
                                    >
                                        🔄 Генератор
                                    </button>
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label>Тип записи</label>
                                <select
                                    value={passwordForm.write_type}
                                    onChange={(e) => setPasswordForm({...passwordForm, write_type: e.target.value})}
                                    className={styles.select}
                                >
                                    {writeTypes.map(type => (
                                        <option key={type} value={type}>
                                            {type.toUpperCase()}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className={styles.modalFooter}>
                            <button className={styles.cancelButton} onClick={() => setShowPasswordModal(false)}>
                                Отмена
                            </button>
                            <button className={styles.saveButton} onClick={handleSavePassword}>
                                {editingPassword ? 'Сохранить' : 'Создать'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PasswordPage;